import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import {
  Dumbbell, ArrowLeft, Mail, Shield, Users, ChevronRight,
  Briefcase, AlertTriangle, HelpCircle
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT ARTICLE DATA — all 16 articles
// ═══════════════════════════════════════════════════════════════════════════════
export const SUPPORT_ARTICLES = {
  // ── Account & Profile ─────────────────────────────────────────────────────
  'sign-in': {
    category: 'Account & Profile',
    title: 'Signing In or Creating an Account',
    content: [
      { type: 'p', text: 'OnlyMass uses Google authentication for a secure, seamless login experience. Here\'s how to get started.' },
      { type: 'h3', text: 'Creating a New Account' },
      { type: 'ol', items: [
        'Go to the OnlyMass homepage and click "Get Started Free" or "Log In".',
        'You\'ll be redirected to the Google sign-in page.',
        'Choose your Google account or enter your Google email and password.',
        'Once authenticated, you\'ll be redirected back to OnlyMass with your account created automatically.',
        'Your name and profile picture from Google will be used as defaults — you can change them anytime in your profile settings.',
      ]},
      { type: 'h3', text: 'Signing In to an Existing Account' },
      { type: 'ol', items: [
        'Click "Log In" on the homepage.',
        'Select the same Google account you used to create your OnlyMass account.',
        'You\'ll be signed in and taken to your dashboard.',
      ]},
      { type: 'h3', text: 'Troubleshooting' },
      { type: 'ul', items: [
        'Make sure you\'re using the same Google account you originally signed up with.',
        'If you see a blank screen after login, try clearing your browser cookies and signing in again.',
        'Check that third-party cookies are not blocked in your browser settings.',
        'If you\'re using a VPN, try disabling it temporarily.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your Google email address and a description of the issue, including any error messages you see.' },
    ],
  },
  'profile-settings': {
    category: 'Account & Profile',
    title: 'Updating Your Profile or Settings',
    content: [
      { type: 'p', text: 'Your profile is how other users and creators see you on OnlyMass. Here\'s how to keep it updated.' },
      { type: 'h3', text: 'Editing Your Profile' },
      { type: 'ol', items: [
        'Log in and go to your dashboard.',
        'Click your avatar/name in the top-right corner to open the dropdown menu.',
        'Select "Settings" to open the Settings modal.',
        'In the Profile tab, you can update your name, bio, profile picture, cover photo, and featured photos/videos.',
        'Click "Save Changes" when you\'re done.',
      ]},
      { type: 'h3', text: 'Adding Social Links' },
      { type: 'p', text: 'If you\'re a creator, you can add links to your Instagram, YouTube, TikTok, and personal website from the profile editor in your account settings.' },
      { type: 'h3', text: 'Changing Your Profile Picture' },
      { type: 'ol', items: [
        'Open Settings from the dropdown menu.',
        'Click on your current profile picture or the upload area.',
        'Select a new image (JPEG, PNG, or WebP, max 10MB).',
        'The image uploads immediately. Click "Save Changes" to confirm.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email and a screenshot of any errors.' },
    ],
  },
  'privacy-visibility': {
    category: 'Account & Profile',
    title: 'Privacy and Visibility Controls',
    content: [
      { type: 'p', text: 'OnlyMass gives you control over who can see your profile and content. Here\'s how to manage your privacy settings.' },
      { type: 'h3', text: 'Accessing Privacy Settings' },
      { type: 'ol', items: [
        'Open your dropdown menu by clicking your avatar in the top-right corner.',
        'Select "Privacy Settings" from the menu.',
        'The Privacy Settings modal shows all available visibility controls.',
      ]},
      { type: 'h3', text: 'What You Can Control' },
      { type: 'ul', items: [
        'Profile Visibility — Choose who can see your profile (Public, Subscribers Only, or Private).',
        'Profile Photo Visibility — Control who sees your profile photo.',
        'Bio Visibility — Control who can read your bio.',
        'Workout Posts Visibility — Set visibility for your workout-related posts.',
        'Videos Visibility — Control access to your video content.',
        'Progress Photos Visibility — Set who can see your progress photos.',
        'Programs & Plans Visibility — Control who sees your workout and meal plans.',
      ]},
      { type: 'h3', text: 'How Visibility Levels Work' },
      { type: 'ul', items: [
        'Public — Anyone can see this content, even without an account.',
        'Subscribers — Only users who subscribe to you can see this content.',
        'Private — Only you can see this content.',
      ]},
      { type: 'h3', text: 'Saving Your Settings' },
      { type: 'p', text: 'Click "Save Settings" after making changes. Your settings are applied immediately and persist across sessions.' },
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email and details about the visibility issue.' },
    ],
  },
  'delete-account': {
    category: 'Account & Profile',
    title: 'Deleting or Deactivating Your Account',
    content: [
      { type: 'p', text: 'If you\'d like to delete your OnlyMass account, here\'s what to know.' },
      { type: 'h3', text: 'Before You Delete' },
      { type: 'ul', items: [
        'Active subscriptions will be cancelled. You will not be charged again.',
        'Purchased workout plans and meal plans will no longer be accessible.',
        'If you\'re a creator, any active subscriber relationships and payouts in progress will be affected.',
        'Your posts, content, and profile will be permanently removed.',
        'This action cannot be undone.',
      ]},
      { type: 'h3', text: 'How to Request Account Deletion' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net from the email address associated with your account.',
        'Include the subject line: "Account Deletion Request".',
        'Include your full name and the email used to sign in.',
        'Our team will confirm your identity and process the deletion within 7 business days.',
      ]},
      { type: 'h3', text: 'What Happens After Deletion' },
      { type: 'ul', items: [
        'Your profile, posts, and content are permanently deleted.',
        'Your subscription and purchase history is removed.',
        'If you had a creator account, your Stripe Connect account remains on Stripe\'s side — contact Stripe directly to close it.',
        'You can create a new OnlyMass account in the future, but your previous data will not be recoverable.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Email support@onlymass.net with any questions before deleting your account.' },
    ],
  },

  // ── Subscriptions & Purchases ─────────────────────────────────────────────
  'manage-subscriptions': {
    category: 'Subscriptions & Purchases',
    title: 'Managing Active Subscriptions',
    content: [
      { type: 'p', text: 'OnlyMass lets you subscribe to fitness creators for exclusive content access. Here\'s how to manage your subscriptions.' },
      { type: 'h3', text: 'Viewing Your Active Subscriptions' },
      { type: 'ol', items: [
        'Log in to your dashboard.',
        'Navigate to a creator\'s profile that you\'re subscribed to.',
        'Your subscription status is shown on their profile (e.g., "Subscribed").',
      ]},
      { type: 'h3', text: 'Cancelling a Subscription' },
      { type: 'ol', items: [
        'Go to the creator\'s profile page.',
        'Click the subscription management button (visible if you have an active subscription).',
        'Confirm cancellation. Your access continues until the end of the current billing period.',
        'You will not be charged again after cancellation.',
      ]},
      { type: 'h3', text: 'Important Notes' },
      { type: 'ul', items: [
        'Subscriptions are billed monthly via Stripe.',
        'You keep access to subscriber content until your current period ends.',
        'You can re-subscribe at any time from the creator\'s profile.',
        'Purchased plans (one-time purchases) are not affected by subscription cancellation.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email and the creator name you\'re having trouble with.' },
    ],
  },
  'access-purchased-plans': {
    category: 'Subscriptions & Purchases',
    title: 'Accessing Purchased Plans',
    content: [
      { type: 'p', text: 'When you purchase a workout plan or meal plan on OnlyMass, it\'s added to your library. Here\'s how to access them.' },
      { type: 'h3', text: 'Finding Your Purchased Plans' },
      { type: 'ol', items: [
        'Log in to your dashboard.',
        'Click the "My Workouts" tab to see purchased and saved workout plans.',
        'Click the "My Meals" tab to see purchased and saved meal plans.',
        'Purchased plans are marked with a "Purchased" badge.',
      ]},
      { type: 'h3', text: 'What\'s Included in a Purchased Plan' },
      { type: 'ul', items: [
        'Full access to all exercises, sets, reps, and instructions (for workout plans).',
        'Full access to all meals, ingredients, macros, and prep instructions (for meal plans).',
        'Any media (photos, videos) attached to the plan.',
        'Permanent access — one-time purchases don\'t expire.',
      ]},
      { type: 'h3', text: 'Plan Not Showing Up?' },
      { type: 'ul', items: [
        'Check that the payment completed successfully (check your email for a receipt).',
        'Try refreshing the page or logging out and back in.',
        'Make sure you\'re logged in with the same account you used to purchase.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email, the plan name, and approximate purchase date.' },
    ],
  },
  'payment-issues': {
    category: 'Subscriptions & Purchases',
    title: 'Payment Issues or Failed Charges',
    content: [
      { type: 'p', text: 'All payments on OnlyMass are processed securely through Stripe. If you\'re experiencing payment issues, here\'s what to check.' },
      { type: 'h3', text: 'Common Payment Issues' },
      { type: 'ul', items: [
        'Insufficient funds — Make sure your payment method has enough balance.',
        'Expired card — Update your payment method if your card has expired.',
        'Bank decline — Your bank may be blocking the transaction. Contact your bank to authorize it.',
        'Currency issues — OnlyMass processes payments in USD.',
        'Browser issues — Try using a different browser or clearing your cookies.',
      ]},
      { type: 'h3', text: 'Updating Your Payment Method' },
      { type: 'p', text: 'Payment methods are managed through Stripe. When you make a new purchase or subscription, you can enter updated card details at checkout.' },
      { type: 'h3', text: 'Duplicate Charges' },
      { type: 'p', text: 'If you see a duplicate charge, wait 24 hours — pending charges often resolve automatically. If the duplicate persists, contact us with the charge details.' },
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email, the amount charged, the date of the charge, and the last 4 digits of the card used.' },
    ],
  },
  'refunds': {
    category: 'Subscriptions & Purchases',
    title: 'Refund Requests',
    content: [
      { type: 'p', text: 'Here\'s how refunds work on OnlyMass.' },
      { type: 'h3', text: 'Refund Eligibility' },
      { type: 'ul', items: [
        'Digital products (workout plans, meal plans) are generally non-refundable once accessed, as they are delivered instantly upon purchase.',
        'Subscriptions that were just charged may be eligible for a refund if requested within 48 hours and the subscriber content was not accessed.',
        'Technical issues (e.g., payment processed but access not granted) are always eligible for resolution or refund.',
      ]},
      { type: 'h3', text: 'How to Request a Refund' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net.',
        'Use the subject line: "Refund Request".',
        'Include your account email, the product or subscription name, the date of purchase, and the reason for the refund.',
        'Our team will review your request and respond within 3 business days.',
      ]},
      { type: 'h3', text: 'Refund Processing' },
      { type: 'p', text: 'Approved refunds are processed back to the original payment method via Stripe. It may take 5–10 business days for the refund to appear on your statement, depending on your bank.' },
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with any questions about refund eligibility before purchasing.' },
    ],
  },

  // ── Creator & Payouts ─────────────────────────────────────────────────────
  'stripe-connect-payouts': {
    category: 'Creator & Payouts',
    title: 'Setting Up Stripe Connect Payouts',
    content: [
      { type: 'p', text: 'OnlyMass uses Stripe Connect to pay creators. Here\'s how to set up your payouts.' },
      { type: 'h3', text: 'Prerequisites' },
      { type: 'ul', items: [
        'You must have an OnlyMass account with creator mode activated.',
        'You need a valid bank account or debit card for receiving payouts.',
      ]},
      { type: 'h3', text: 'Setting Up Stripe Connect' },
      { type: 'ol', items: [
        'Log in to your OnlyMass dashboard.',
        'Click your avatar and select "Professional Mode" from the dropdown.',
        'In the Payout Setup section, click "Connect with Stripe" or "Set Up Payouts".',
        'You\'ll be redirected to Stripe\'s onboarding flow.',
        'Complete Stripe\'s identity verification, enter your bank details, and agree to their terms.',
        'Once complete, you\'ll be redirected back to OnlyMass with payouts enabled.',
      ]},
      { type: 'h3', text: 'Checking Your Payout Status' },
      { type: 'p', text: 'Open Professional Mode from the dropdown menu. The Payout Setup section shows your current Stripe Connect status, including whether charges and payouts are enabled.' },
      { type: 'h3', text: 'Troubleshooting' },
      { type: 'ul', items: [
        'If Stripe onboarding is incomplete, re-open Professional Mode and click the setup button again to resume.',
        'Make sure you\'ve completed all required identity verification steps on Stripe\'s side.',
        'If your payouts show as "Pending", Stripe may need additional information — check your email for messages from Stripe.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with your account email and a screenshot of your Payout Setup screen.' },
    ],
  },
  'commission-rates': {
    category: 'Creator & Payouts',
    title: 'Understanding Commission Rates',
    content: [
      { type: 'p', text: 'OnlyMass takes a platform commission on creator earnings. Here\'s how the rates work.' },
      { type: 'h3', text: 'Commission Tiers' },
      { type: 'ul', items: [
        'Standard Rate: 20% platform commission — you keep 80% of all revenue.',
        'Early Adopter Rate: 15% platform commission (you keep 85%) — applies for the first 3 months after activating creator mode.',
        'Founding Creator Rate: 15% commission permanently — available to qualifying early members.',
      ]},
      { type: 'h3', text: 'Where Commission Applies' },
      { type: 'p', text: 'The platform commission applies to all creator revenue: subscriptions, workout plan sales, meal plan sales, and tips. Stripe\'s payment processing fees apply separately on top.' },
      { type: 'h3', text: 'Checking Your Current Rate' },
      { type: 'ol', items: [
        'Open Professional Mode from your dropdown menu.',
        'Your current commission rate is displayed in the Payout Setup section.',
        'If you\'re on the Early Adopter rate, the transition date to the standard rate is shown.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with any questions about your commission tier.' },
    ],
  },
  'analytics': {
    category: 'Creator & Payouts',
    title: 'Creator Analytics and Metrics',
    content: [
      { type: 'p', text: 'OnlyMass provides a built-in analytics dashboard for creators to track performance. Here\'s how to use it.' },
      { type: 'h3', text: 'Accessing Your Analytics' },
      { type: 'ol', items: [
        'Click your avatar in the top-right corner.',
        'Select "Professional Mode" from the dropdown.',
        'The Creator Analytics section shows all your metrics.',
      ]},
      { type: 'h3', text: 'Available Metrics' },
      { type: 'ul', items: [
        'Followers — Total number of users following you.',
        'Subscribers — Active and total subscribers.',
        'Revenue — Total earnings and revenue breakdown by source.',
        'Profile Views — How many times your profile has been viewed.',
        'Posts — Total number of posts you\'ve published.',
        'Workout Plans & Meal Plans — Count of your published products.',
        'Engagement — Total likes and comments on your posts.',
        'Bookmarks — How many times your products have been saved by users.',
        'Purchases — Total number of product purchases.',
      ]},
      { type: 'h3', text: 'Time Filters' },
      { type: 'p', text: 'Use the time period selector to view metrics for All Time, Last 30 Days, or Last 7 Days. Period-specific stats show new followers, subscribers, and revenue for that window.' },
      { type: 'h3', text: 'Charts' },
      { type: 'p', text: 'The analytics include an Engagement Over Time chart (likes and comments daily), a Follower Growth chart, a Revenue Breakdown, Top Performing Content, and a Recent Activity feed.' },
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net if your analytics appear incorrect or are not loading.' },
    ],
  },
  'content-publishing': {
    category: 'Creator & Payouts',
    title: 'Content Creation and Publishing',
    content: [
      { type: 'p', text: 'As an OnlyMass creator, you can publish posts, workout plans, and meal plans. Here\'s how.' },
      { type: 'h3', text: 'Creating a Post' },
      { type: 'ol', items: [
        'From your dashboard Feed tab, click the "Create Post" button.',
        'Add a title, content text, and optionally upload a photo or video.',
        'Select a post type (general, workout, nutrition, etc.).',
        'Choose fitness niches that match your content.',
        'Set the access type: Public (everyone), Subscriber-only, or other options.',
        'Click "Publish" to make it live.',
      ]},
      { type: 'h3', text: 'Creating a Workout Plan' },
      { type: 'ol', items: [
        'From the dashboard, open the workout plan creation dialog.',
        'Fill in the title, description, difficulty level, and duration.',
        'Add exercises with sets, reps, rest times, and instructions.',
        'Select fitness niches for discoverability.',
        'Set your audience (Public or Subscribers) and pricing (Free or One-Time Purchase with a price).',
        'Publish the plan.',
      ]},
      { type: 'h3', text: 'Creating a Meal Plan' },
      { type: 'p', text: 'The process is similar to workout plans. Add meals with ingredients, macros, instructions, and prep time. Set nutrition categories for discovery.' },
      { type: 'h3', text: 'Uploading Media' },
      { type: 'ul', items: [
        'Supported image formats: JPEG, PNG, WebP, GIF (max 10MB).',
        'Supported video formats: MP4, MOV, WebM (max 50MB).',
        'Videos automatically generate a poster/thumbnail preview.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net with details about the content you\'re trying to publish and any errors.' },
    ],
  },

  // ── Safety & Reporting ────────────────────────────────────────────────────
  'report-content': {
    category: 'Safety & Reporting',
    title: 'Reporting Inappropriate Content',
    content: [
      { type: 'p', text: 'OnlyMass is committed to maintaining a safe, professional fitness environment. If you see content that violates our Community Guidelines, please report it.' },
      { type: 'h3', text: 'What Should Be Reported' },
      { type: 'ul', items: [
        'Content promoting dangerous or harmful practices.',
        'Spam, scams, or deceptive content.',
        'Content unrelated to fitness, health, or wellness.',
        'Content that violates our Community Guidelines.',
      ]},
      { type: 'h3', text: 'How to Report' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net.',
        'Use the subject line: "Content Report".',
        'Include the URL or location of the content, the creator\'s name, a description of the violation, and screenshots if possible.',
      ]},
      { type: 'h3', text: 'What Happens After Reporting' },
      { type: 'ul', items: [
        'Our team reviews reports within 48 hours.',
        'If the content violates our guidelines, it will be removed.',
        'The creator may receive a warning or account action depending on severity.',
        'You will not be notified of the outcome for privacy reasons, but action will be taken.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'For urgent safety concerns, email support@onlymass.net with "URGENT" in the subject line.' },
    ],
  },
  'copyright-dmca': {
    category: 'Safety & Reporting',
    title: 'Copyright or DMCA Concerns',
    content: [
      { type: 'p', text: 'If you believe content on OnlyMass infringes your copyright, you can file a DMCA takedown request.' },
      { type: 'h3', text: 'Filing a DMCA Takedown Request' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net.',
        'Include a description of the copyrighted work that was infringed.',
        'Include the URL of the infringing content on OnlyMass.',
        'Include your contact information (name, email, mailing address).',
        'Include a statement of good faith belief that the use is unauthorized.',
        'Include a statement under penalty of perjury that your notice is accurate.',
        'Include your physical or electronic signature.',
      ]},
      { type: 'h3', text: 'What Happens Next' },
      { type: 'ul', items: [
        'We review DMCA requests within 48 hours.',
        'Valid requests result in content removal.',
        'The content creator is notified and may file a counter-notification.',
        'Repeat infringers may have their accounts terminated.',
      ]},
      { type: 'p', text: 'For full details, see our Copyright / DMCA Policy page.' },
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net for all intellectual property matters.' },
    ],
  },
  'harassment-abuse': {
    category: 'Safety & Reporting',
    title: 'Harassment or Abuse Reports',
    content: [
      { type: 'p', text: 'OnlyMass has zero tolerance for harassment, bullying, or abuse. Here\'s how to report it.' },
      { type: 'h3', text: 'What Counts as Harassment' },
      { type: 'ul', items: [
        'Threatening or intimidating messages or comments.',
        'Bullying or repeated unwanted contact.',
        'Hate speech or discrimination.',
        'Sexual harassment.',
        'Doxxing or sharing someone\'s private information.',
      ]},
      { type: 'h3', text: 'How to Report Harassment' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net.',
        'Use the subject line: "Harassment Report".',
        'Include the username or profile of the person involved.',
        'Include screenshots of the harassment (messages, comments, etc.).',
        'Include dates and details of the incidents.',
        'Include any other relevant context.',
      ]},
      { type: 'h3', text: 'What We Do' },
      { type: 'ul', items: [
        'We investigate all harassment reports seriously.',
        'Actions may include content removal, warnings, temporary suspension, or permanent ban.',
        'Your identity is kept confidential throughout the process.',
        'We aim to respond to harassment reports within 24 hours.',
      ]},
      { type: 'h3', text: 'Immediate Safety' },
      { type: 'p', text: 'If you feel unsafe, please also contact local authorities. For urgent safety concerns, include "URGENT" in your subject line.' },
    ],
  },
  'impersonation-fraud': {
    category: 'Safety & Reporting',
    title: 'Impersonation or Fraud',
    content: [
      { type: 'p', text: 'If someone is impersonating you or another person on OnlyMass, or if you suspect fraudulent activity, report it immediately.' },
      { type: 'h3', text: 'Signs of Impersonation' },
      { type: 'ul', items: [
        'An account using your name, photos, or likeness without permission.',
        'An account pretending to be a well-known fitness creator.',
        'An account copying another creator\'s content and claiming it as their own.',
      ]},
      { type: 'h3', text: 'Signs of Fraud' },
      { type: 'ul', items: [
        'Accounts selling fake workout or meal plans.',
        'Phishing attempts asking for your login credentials.',
        'Accounts promising results they cannot deliver.',
        'Requests for payment outside the OnlyMass platform.',
      ]},
      { type: 'h3', text: 'How to Report' },
      { type: 'ol', items: [
        'Send an email to support@onlymass.net.',
        'Use the subject line: "Impersonation Report" or "Fraud Report".',
        'Include the URL of the suspicious account or content.',
        'Include screenshots and any evidence.',
        'If you\'re reporting impersonation of yourself, include proof of your identity (links to your real social media profiles, etc.).',
      ]},
      { type: 'h3', text: 'What Happens Next' },
      { type: 'ul', items: [
        'We investigate reports within 48 hours.',
        'Confirmed impersonation accounts are permanently removed.',
        'Fraudulent accounts are banned and reported where appropriate.',
      ]},
      { type: 'h3', text: 'Still Need Help?' },
      { type: 'p', text: 'Contact support@onlymass.net. For impersonation of a public figure, include "URGENT" in the subject.' },
    ],
  },
};

// Category → slugs mapping (for the support landing page)
export const SUPPORT_CATEGORIES = [
  {
    icon: Users,
    title: 'Account & Profile',
    description: 'Help with your OnlyMass account',
    articles: [
      { slug: 'sign-in', label: 'Signing in or creating an account' },
      { slug: 'profile-settings', label: 'Updating your profile or settings' },
      { slug: 'privacy-visibility', label: 'Privacy and visibility controls' },
      { slug: 'delete-account', label: 'Deleting or deactivating your account' },
    ],
  },
  {
    icon: Shield,
    title: 'Subscriptions & Purchases',
    description: 'Billing, payments, and access',
    articles: [
      { slug: 'manage-subscriptions', label: 'Managing active subscriptions' },
      { slug: 'access-purchased-plans', label: 'Accessing purchased plans' },
      { slug: 'payment-issues', label: 'Payment issues or failed charges' },
      { slug: 'refunds', label: 'Refund requests' },
    ],
  },
  {
    icon: Briefcase,
    title: 'Creator & Payouts',
    description: 'For creators using OnlyMass',
    articles: [
      { slug: 'stripe-connect-payouts', label: 'Setting up Stripe Connect payouts' },
      { slug: 'commission-rates', label: 'Understanding commission rates' },
      { slug: 'analytics', label: 'Creator analytics and metrics' },
      { slug: 'content-publishing', label: 'Content creation and publishing' },
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Safety & Reporting',
    description: 'Report issues or policy violations',
    articles: [
      { slug: 'report-content', label: 'Reporting inappropriate content' },
      { slug: 'copyright-dmca', label: 'Copyright or DMCA concerns' },
      { slug: 'harassment-abuse', label: 'Harassment or abuse reports' },
      { slug: 'impersonation-fraud', label: 'Impersonation or fraud' },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT ARTICLE PAGE — renders a single article from the data
// ═══════════════════════════════════════════════════════════════════════════════
const ArticleBlock = ({ block }) => {
  if (block.type === 'p') return <p className="text-slate-600 leading-relaxed mb-4">{block.text}</p>;
  if (block.type === 'h3') return <h3 className="text-lg font-semibold text-[#0f1d3d] mt-8 mb-3">{block.text}</h3>;
  if (block.type === 'ul') return (
    <ul className="space-y-2 mb-4 ml-1">
      {block.items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
  if (block.type === 'ol') return (
    <ol className="space-y-2 mb-4 ml-1">
      {block.items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
          <span className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center text-xs font-semibold text-[#0f1d3d] flex-shrink-0 mt-0.5">{i + 1}</span>
          {item}
        </li>
      ))}
    </ol>
  );
  return null;
};

export const SupportArticlePage = () => {
  const { slug } = useParams();
  const article = SUPPORT_ARTICLES[slug];

  if (!article) return <Navigate to="/support" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link to="/support" className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#0f1d3d] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Support
          </Link>
          <div className="h-5 w-px bg-slate-200" />
          <Link to="/" className="flex items-center gap-1.5">
            <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-6 h-6 rounded" />
            <span className="font-bold text-[#0f1d3d] text-sm">OnlyMass</span>
          </Link>
        </div>
      </nav>

      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-sm font-medium text-blue-600 mb-2">{article.category}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f1d3d]">{article.title}</h1>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {article.content.map((block, i) => <ArticleBlock key={i} block={block} />)}

        <div className="mt-10 pt-6 border-t border-slate-200">
          <Card>
            <CardContent className="p-5 flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-lg flex-shrink-0">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-[#0f1d3d]">support@onlymass.net</p>
                <p className="text-sm text-slate-500">Include your account email and a description of your issue.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6">
          <Link to="/support" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to all support topics
          </Link>
        </div>
      </main>
    </div>
  );
};
